import React, { useEffect, useState } from "react";
import { api, euro, formatApiErrorDetail } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { Tag, Save, Loader2, Signal, Gift, BadgePercent } from "lucide-react";

const ORDER = ["oa_pec_1y", "oa_postal_raccomandata", "oa_realestate_valuation", "oa_pagopa"];

export default function AdminServicePrices() {
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [esimMk, setEsimMk] = useState("");
  const [savingEsim, setSavingEsim] = useState(false);
  const [gc, setGc] = useState({ default_master: "", default_dealer: "" });
  const [savingGc, setSavingGc] = useState(false);

  const load = () => api.get("/openapi/service-prices").then((r) => setData(r.data || {})).catch(() => {}).finally(() => setLoading(false));
  useEffect(() => {
    load();
    api.get("/settings/airalo").then((r) => setEsimMk(String(r.data?.markup_percent ?? ""))).catch(() => {});
    api.get("/settings/reloadly-giftcard-margins").then((r) => setGc({ default_master: String(r.data?.default_master ?? ""), default_dealer: String(r.data?.default_dealer ?? "") })).catch(() => {});
  }, []);

  const setField = (sid, k, v) => setData((d) => ({ ...d, [sid]: { ...d[sid], [k]: v } }));

  const save = async () => {
    setSaving(true);
    try {
      const prices = {};
      ORDER.forEach((sid) => { if (data[sid]) prices[sid] = { price: salePrice(sid) ?? data[sid].price, cost: data[sid].cost, markup_percent: data[sid].markup_percent }; });
      const { data: fresh } = await api.put("/admin/openapi/service-prices", { prices });
      setData(fresh);
      toast.success("Prezzi servizi aggiornati");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Errore salvataggio"); }
    finally { setSaving(false); }
  };

  const saveEsim = async () => {
    setSavingEsim(true);
    try {
      await api.put("/settings/airalo", { markup_percent: parseFloat(esimMk) || 0 });
      toast.success("Markup eSIM aggiornato");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Errore salvataggio"); }
    finally { setSavingEsim(false); }
  };

  const saveGc = async () => {
    setSavingGc(true);
    try {
      const { data: fresh } = await api.put("/settings/reloadly-giftcard-margins", { default_master: parseFloat(gc.default_master) || 0, default_dealer: parseFloat(gc.default_dealer) || 0 });
      setGc({ default_master: String(fresh.default_master), default_dealer: String(fresh.default_dealer) });
      toast.success("Margini gift card aggiornati");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Errore salvataggio"); }
    finally { setSavingGc(false); }
  };

  const salePrice = (sid) => { const c = parseFloat(data[sid]?.cost), m = parseFloat(data[sid]?.markup_percent); return !isNaN(c) && !isNaN(m) ? Math.round(c * (1 + m / 100) * 1.22 * 100) / 100 : null; };
  const margin = (sid) => { const p = salePrice(sid) ?? parseFloat(data[sid]?.price), c = parseFloat(data[sid]?.cost); return !isNaN(p) && !isNaN(c) ? euro(p / 1.22 - c) : "—"; };

  return (
    <div className="space-y-6" data-testid="admin-service-prices-page">
      <div>
        <p className="text-xs font-medium tracking-wider uppercase text-muted-foreground">Servizi & Fornitori</p>
        <h1 className="font-head text-3xl font-bold tracking-tight mt-1 flex items-center gap-2"><Tag className="w-7 h-7 text-primary" /> Prezzi & Margini Servizi</h1>
        <p className="text-sm text-muted-foreground mt-1">Un unico posto per prezzi/costi dei servizi OpenAPI, markup eSIM e margini gift card.</p>
      </div>

      <Card className="glow-border p-5 space-y-4">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <h2 className="font-head text-lg font-semibold flex items-center gap-2"><Tag className="w-5 h-5 text-primary" /> Servizi OpenAPI</h2>
          <Button onClick={save} disabled={saving || loading} className="gap-1" data-testid="service-prices-save"><Save className="w-4 h-4" />{saving ? "Salvo…" : "Salva prezzi"}</Button>
        </div>
        {loading ? <div className="flex items-center gap-2 text-muted-foreground"><Loader2 className="w-4 h-4 animate-spin" /> Carico…</div> : (
          <Table>
            <TableHeader><TableRow>
              <TableHead>Servizio</TableHead><TableHead>Costo fornitore netto (€)</TableHead><TableHead>Ricarico (%)</TableHead><TableHead>Prezzo al dealer IVA inclusa (€)</TableHead><TableHead>Guadagno netto</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {ORDER.filter((sid) => data[sid]).map((sid) => (
                <TableRow key={sid} data-testid={`service-price-row-${sid}`}>
                  <TableCell className="font-medium">{data[sid].name}</TableCell>
                  <TableCell><Input type="number" step="0.01" min="0" className="w-32" data-testid={`service-cost-${sid}`} value={data[sid].cost ?? ""} onChange={(e) => setField(sid, "cost", e.target.value)} placeholder="—" /></TableCell>
                  <TableCell><Input type="number" step="0.1" min="0" className="w-24" data-testid={`service-markup-${sid}`} value={data[sid].markup_percent ?? ""} onChange={(e) => setField(sid, "markup_percent", e.target.value)} placeholder="%" /></TableCell>
                  <TableCell className="font-semibold" data-testid={`service-price-${sid}`}>{euro(salePrice(sid) ?? data[sid].price)}</TableCell>
                  <TableCell className="text-teal-500 font-medium" data-testid={`service-margin-${sid}`}>{margin(sid)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>

      <div className="grid md:grid-cols-2 gap-6">
        <Card className="glow-border p-5 space-y-4" data-testid="esim-markup-card">
          <h2 className="font-head text-lg font-semibold flex items-center gap-2"><Signal className="w-5 h-5 text-primary" /> Markup eSIM (Airalo)</h2>
          <p className="text-sm text-muted-foreground">Ricarico base applicato al costo netto dei pacchetti eSIM. Le fasce per costo restano in Integrazioni → Airalo.</p>
          <div className="max-w-xs">
            <Label className="flex items-center gap-1"><BadgePercent className="w-3.5 h-3.5" /> Markup base (%)</Label>
            <Input type="number" step="0.1" min="0" className="mt-1.5" data-testid="esim-markup-input" value={esimMk} onChange={(e) => setEsimMk(e.target.value)} />
          </div>
          <Button onClick={saveEsim} disabled={savingEsim} className="gap-1" data-testid="esim-markup-save"><Save className="w-4 h-4" />{savingEsim ? "Salvo…" : "Salva markup eSIM"}</Button>
        </Card>

        <Card className="glow-border p-5 space-y-4" data-testid="giftcard-defaults-card">
          <h2 className="font-head text-lg font-semibold flex items-center gap-2"><Gift className="w-5 h-5 text-primary" /> Margini Gift Card (default)</h2>
          <p className="text-sm text-muted-foreground">Margine di default per ruolo. Gli override per singolo brand restano in "Categorie & Provvigioni".</p>
          <div className="grid grid-cols-2 gap-3 max-w-sm">
            <div><Label>Master (%)</Label><Input type="number" step="0.1" min="0" className="mt-1.5" data-testid="gc-default-master-input" value={gc.default_master} onChange={(e) => setGc((g) => ({ ...g, default_master: e.target.value }))} /></div>
            <div><Label>Dealer (%)</Label><Input type="number" step="0.1" min="0" className="mt-1.5" data-testid="gc-default-dealer-input" value={gc.default_dealer} onChange={(e) => setGc((g) => ({ ...g, default_dealer: e.target.value }))} /></div>
          </div>
          <Button onClick={saveGc} disabled={savingGc} className="gap-1" data-testid="gc-defaults-save"><Save className="w-4 h-4" />{savingGc ? "Salvo…" : "Salva margini gift card"}</Button>
        </Card>
      </div>
    </div>
  );
}
